
import React, { useState, useEffect, useRef } from 'react';
import {
    AddContainer, Component4, Texts, InputTextComponent,
    PrescriptionViewBox, PresText, PresTitle, EditButton, SaveButton, LabelWithTooltip
} from './Styles';
import InputSelect from 'src/components/shared/Form/Select';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, Chip, Stack } from '@mui/material';
import { getTemplateByClinicIdRequest, getClinicByIdRequest, getDoctorProfileByIdRequest, getPatientRequest } from 'src/modules/app/store/appActions';
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import { ReactComponent as Close } from '../../../../../assets/images/close-circle.svg';
import theme from 'src/config/theme';
import useResponsive from 'src/components/hooks/useResponsive';
import { getUserFromToken } from 'src/DecodeToken';
import dayjs from 'dayjs';

const Language = [
    { label: 'English', value: 'english' },
    { label: 'Hindi', value: 'hindi' },
    { label: 'Gujarati', value: 'gujarati' },
];

const VARIABLE_GROUPS = [
    {
        label: 'Clinic Details',
        variables: [
            { label: 'Clinic Name', value: '{{clinic_name}}' },
            { label: 'Clinic Address', value: '{{clinic_address}}' },
            { label: 'Clinic Phone', value: '{{clinic_phone}}' },
        ]
    },
    {
        label: 'Patient Details',
        variables: [
            { label: 'Patient Name', value: '{{patient_name}}' },
            { label: 'Patient Age', value: '{{patient_age}}' },
            { label: 'Patient Gender', value: '{{patient_gender}}' },
        ]
    },
    {
        label: 'Doctor Details',
        variables: [
            { label: 'Doctor Name', value: '{{doctor_name}}' },
            { label: 'Specialty', value: '{{doctor_specialty}}' },
        ]
    }
];

const MessageTemplateStep = ({ innerRef, data, setClinicProfileData, clinicId }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [localTemplates, setLocalTemplates] = useState(data || []);
    const [openDialog, setOpenDialog] = useState(false);
    const [previewData, setPreviewData] = useState({
        clinic: null,
        doctor: null,
        patient: null
    });
    const textRef = useRef(null);
    const dispatch = useDispatch();
    const TemplateData = useSelector((state) => state.app.getTemplateByClinicId);
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '20px';
    const Height = isMobile || isTablet ? '18px' : '20px';

    useEffect(() => {
        if (clinicId) {
            dispatch(getTemplateByClinicIdRequest(clinicId));
            // Fetch clinic details for preview
            dispatch(getClinicByIdRequest(clinicId, (res) => {
                setPreviewData(prev => ({ ...prev, clinic: res?.data || res }));
            }));
        }
    }, [clinicId, dispatch]);

    useEffect(() => {
        const userId = getUserFromToken();
        if (userId) {
            // Fetch doctor details for preview
            dispatch(getDoctorProfileByIdRequest(userId, (res) => {
                setPreviewData(prev => ({ ...prev, doctor: res?.data || res }));
            }));
        }
    }, [dispatch]);

    useEffect(() => {
        // Fetch last registered patient for preview
        dispatch(getPatientRequest({ params: { limit: 1, sort: 'createdAt:desc' } }, (res) => {
            if (res && res.data && res.data.length > 0) {
                setPreviewData(prev => ({ ...prev, patient: res.data[0] }));
            }
        }));
    }, [dispatch]);
    // useEffect(() => {
    //     if (TemplateData?.payload?.data) {
    //         const filtered = TemplateData.payload.data
    //             .filter((template, idx) => template.type === 'message')
    //             .map((template, idx) => ({
    //                 id: template.id || idx,
    //                 label: template.title,
    //                 value: template.title,
    //                 text: template.text,
    //             }));
    //         setLocalTemplates(filtered);
    //     }
    // }, [TemplateData]);

    useEffect(() => {
        if (Array.isArray(data)) {
            const formatted = data.map((template, idx) => ({
                id: template.id || idx,
                label: template.title || template.label,
                value: template.title || template.label,
                text: template.text,
                language: template.language || 'english',
            }));
            setLocalTemplates(formatted);
        }
    }, [data]);
    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setOpenDialog(true);
    };

    const handleDelete = (templateToDelete) => {
        const updatedTemplates = localTemplates.filter(t => t.id !== templateToDelete.id);

        setLocalTemplates(updatedTemplates);

        setClinicProfileData(prevData => ({
            ...prevData,
            messageTemplate: updatedTemplates.map(t => ({
                title: t.label,
                text: t.text,
                type: 'message',
                language: t.language,
            })),
        }));
    };
    const handleAdd = () => {
        setSelectedTemplate(null);
        setOpenDialog(true);
    };
    const handleSubmit = (values) => {
        if (!values.title.trim() || !values.text.trim()) {
            return;
        }
        setLocalTemplates(prev => {
            const isExisting = selectedTemplate
                ? prev.find(t => t.id === selectedTemplate.id)
                : false;

            let updatedTemplates;
            if (isExisting) {
                updatedTemplates = prev.map(t =>
                    t.id === selectedTemplate.id ? {
                        ...t,
                        label: values.title,
                        value: values.title,
                        text: values.text,
                        language: values.language,
                    } : t
                );
            } else {
                const duplicate = prev.find(t =>
                    t.label.trim() === values.title.trim() &&
                    t.text.trim() === values.text.trim() &&
                    t.language === values.language
                );

                if (duplicate) return prev;
                updatedTemplates = [
                    ...prev,
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        label: values.title,
                        value: values.title,
                        text: values.text,
                        language: values.language,
                    }
                ];
            }
            const uniqueTemplates = updatedTemplates.filter((t, index, self) =>
                index === self.findIndex(
                    s => s.label.trim() === t.label.trim() && s.text.trim() === t.text.trim() && s.language === t.language
                )
            );
            setClinicProfileData(prevData => ({
                ...prevData,
                messageTemplate: uniqueTemplates.map(t => ({
                    title: t.label,
                    text: t.text,
                    type: 'message',
                    language: t.language,
                })),
            }));

            return uniqueTemplates;
        });
        setSelectedTemplate(null);
        setOpenDialog(false);
    };
    return (
        <>
            <AddContainer>
                {localTemplates.length > 0 && localTemplates.map((template) => (
                    <PrescriptionViewBox
                        key={template.id}
                    >
                        <Box>
                            <PresTitle>{template.label}</PresTitle>
                            <PresText>{template.text}</PresText>
                            <Typography variant="caption" color="textSecondary">Language: {template.language}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px' }}>
                            <EditButton type="button" onClick={() => handleEdit(template)}>
                                <Edit width={Width} />
                            </EditButton>
                            <EditButton type="button" onClick={() => handleDelete(template)}>
                                <Close width={Width} />
                            </EditButton>
                        </Box>
                    </PrescriptionViewBox>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <SaveButton
                        variant="outlined"
                        startIcon={<Add width={Width} height={Height} sx={{ color: theme.palette.primary.main }} />}
                        onClick={handleAdd}
                    >
                        Add
                    </SaveButton>
                </Box>
            </AddContainer>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md"  >
                <DialogTitle>{selectedTemplate ? 'Edit Template' : 'Add New Template'}</DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={{
                            type: 'message',
                            language: selectedTemplate?.language || Language[0].value,
                            title: selectedTemplate?.label || '',
                            text: selectedTemplate?.text || '',
                        }}
                        enableReinitialize
                        onSubmit={handleSubmit}
                        innerRef={innerRef}
                    >
                        {({ values, handleChange, handleBlur, setFieldValue }) => (
                            <Form>
                                <Component4>
                                    <Texts>
                                        <LabelWithTooltip
                                            label="Language"
                                            tooltip="Select the language for this message template."
                                        />
                                    </Texts>
                                    <InputSelect
                                        name="language"
                                        value={values.language}
                                        options={Language}
                                        onChange={(value) => setFieldValue('language', value)}
                                        variant="outlined"
                                        placeholder="Select"
                                        showNone={false}
                                        sx={{
                                            boxShadow: theme.shadows[1],
                                            '& .MuiOutlinedInput-root': {
                                                height: '52px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderRadius: '12px',
                                            },
                                        }}
                                    />
                                </Component4>

                                <Component4>
                                    <Texts>
                                        <LabelWithTooltip
                                            label="Message Template"
                                            tooltip="Enter a title for this message template (e.g., Welcome Message, Appointment Reminder)."
                                        />
                                    </Texts>
                                    <InputTextComponent
                                        name="title"
                                        variant="outlined"
                                        value={values.title}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </Component4>

                                <Component4>
                                    <Texts>
                                        <LabelWithTooltip
                                            label="Message"
                                            tooltip="Enter the actual message content that will be sent to the patient. You can use custom variables below."
                                        />
                                    </Texts>

                                    <Box sx={{ mb: 2 }}>
                                        {VARIABLE_GROUPS.map((group) => (
                                            <Box key={group.label} sx={{ mb: 1.5 }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                                                    {group.label}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {group.variables.map((variable) => (
                                                        <Chip
                                                            key={variable.value}
                                                            label={variable.label}
                                                            size="small"
                                                            onClick={() => {
                                                                const textarea = textRef.current?.querySelector('textarea');
                                                                if (textarea) {
                                                                    const start = textarea.selectionStart;
                                                                    const end = textarea.selectionEnd;
                                                                    const text = values.text;
                                                                    const before = text.substring(0, start);
                                                                    const after = text.substring(end, text.length);
                                                                    const newValue = before + variable.value + after;
                                                                    setFieldValue('text', newValue);

                                                                    // Focus back and set cursor position after a tick
                                                                    setTimeout(() => {
                                                                        textarea.focus();
                                                                        const cursorPosition = start + variable.value.length;
                                                                        textarea.setSelectionRange(cursorPosition, cursorPosition);
                                                                    }, 0);
                                                                } else {
                                                                    setFieldValue('text', values.text + variable.value);
                                                                }
                                                            }}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                backgroundColor: theme.palette.primary.light3,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.primary.light1,
                                                                },
                                                                mb: 0.5
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Box>

                                    <InputTextComponent
                                        ref={textRef}
                                        name="text"
                                        value={values.text}
                                        variant="outlined"
                                        placeholder="Enter message content..."
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        multiline
                                        rows={6}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                height: 'auto',
                                                minHeight: '120px',
                                                alignItems: 'flex-start',
                                            },
                                        }}
                                    />
                                </Component4>

                                {values.text && (
                                    <Box sx={{ mt: 2, p: 2, borderRadius: '12px', backgroundColor: theme.palette.grey[50], border: `1px dashed ${theme.palette.grey[300]}` }}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                            Live Preview:
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: theme.palette.text.primary }}>
                                            {values.text
                                                .replace(/\{\{clinic_name\}\}/g, previewData.clinic?.clinicName || 'City Dental Clinic')
                                                .replace(/\{\{clinic_address\}\}/g, previewData.clinic?.address || '123 Medical Square, City Center')
                                                .replace(/\{\{clinic_phone\}\}/g, previewData.clinic?.phoneNo || '+91 9876543210')
                                                .replace(/\{\{patient_name\}\}/g, previewData.patient?.fullName || 'John Doe')
                                                .replace(/\{\{patient_age\}\}/g, previewData.patient?.detailedAge?.years || previewData.patient?.age || '28')
                                                .replace(/\{\{patient_gender\}\}/g, previewData.patient?.gender || 'Male')
                                                .replace(/\{\{doctor_name\}\}/g, previewData.doctor?.fullName || 'Dr. Smith')
                                                .replace(/\{\{doctor_specialty\}\}/g, previewData.doctor?.specialization || 'Dentist')}
                                        </Typography>
                                    </Box>
                                )}

                                <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedTemplate(null);
                                            setOpenDialog(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MessageTemplateStep;
